/**
 * Playing a module: its capsules, in order, one after another.
 *
 * ## A capsule is a film and then its questions — and a module is that, six times
 *
 * This is the correction the first build needed. It walked the playlist but it
 * played *only* the questions, and it drew the *Module finished* panel whenever the
 * next capsule's record was still on its way — so a learner mid-course was told
 * their course was over between every pair of capsules, on a screen they should
 * never have seen at all. Both of those are the same mistake: treating a capsule as
 * its quiz and the playlist as a list of quizzes. It is not. A capsule is the thing
 * the feed plays — a **film**, and then the **questions** — and a module is six or
 * more of *those*, one after another.
 *
 * So the run is a two-stage step per capsule, and the rules for it are pure and
 * tested in `#/lib/moduleRun`:
 *
 *  - **It opens on the film.** A capsule whose cover film exists opens on it and
 *    hands over to the questions when it ends. A capsule with no film — one
 *    published before covers existed, or one whose post carries no video — opens on
 *    its questions instead, because waiting for a film that does not exist is a
 *    black screen with no way forward.
 *  - **Finishing the questions leads to the *next capsule's film*** — never to a
 *    summary. The summary belongs to the end of the playlist and to nowhere else.
 *  - **It can be left and come back to.** Nobody watches six films and answers six
 *    quizzes in one sitting. Each finished capsule is remembered on this device
 *    (`#/state/moduleRuns`, because there is no endpoint for a run yet), and opening
 *    the module again continues at the first capsule not yet finished rather than
 *    making the learner sit through the first three again.
 *
 * ## What this screen adds, and what it deliberately reuses
 *
 * The film is `CapsuleFilmPlayer` — the same cover, the same window on it, the same
 * hand-over the capsule's own player uses. The questions are `CapsuleThemePlayer` —
 * the app's *only* capsule player, the one that posts each capsule's play, so a
 * capsule played inside a module is played exactly as it is anywhere else and every
 * play this run produces is a play the module's board can count. What this screen
 * adds is the thing a playlist has and a capsule does not: **the run** — the strip
 * that says `3 / 6`, and the chaining that never stops between capsules.
 *
 * The events are kept apart on purpose. `CapsuleThemePlayer` distinguishes them for
 * us (see its `onCapsuleComplete`): the questions' own close button calls `onClose`
 * — *left*, so the capsule is not counted and the run stays where it was — while
 * finishing the questions calls `onCapsuleComplete` — *done*, so it is remembered
 * and the next film starts.
 *
 * ## The summary does not invent a score
 *
 * The plays this run produces are posted as each capsule is finished, so the numbers
 * come back from the board rather than being counted here a second time — and when
 * the deployment cannot answer (there is no `getCapsulePlays` yet), the summary says
 * the run is done and offers the board, instead of inventing a total.
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {
  AppBskyEmbedVideo,
  AtUri,
  type AppBskyFeedDefs,
} from '@atproto/api'
import {Trans, useLingui} from '@lingui/react/macro'
import {useSafeAreaInsets} from 'react-native-safe-area-context'

import {capsuleCoverFromEmbed} from '#/lib/capsuleCover'
import {CAPSULE_COVER_MAX_MS} from '#/lib/coverTrim'
import {findPlayer, formatRate, formatTime} from '#/lib/moduleLeaderboard'
import {moduleUriFromPath} from '#/lib/moduleRecord'
import {
  moduleOpeningStage,
  moduleStepAfterFilm,
  moduleStepAfterQuiz,
  type ModuleRunStage,
} from '#/lib/moduleRun'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {useModuleRun} from '#/state/moduleRuns'
import {useCapsuleByUriQuery} from '#/state/queries/capsule'
import {useModuleByUriQuery} from '#/state/queries/module'
import {usePostQuery} from '#/state/queries/post'
import {useSession} from '#/state/session'
import {atoms as a, ThemeProvider, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {CapsuleFilmPlayer} from '#/components/capsules/CapsuleFilmPlayer'
import {CapsuleThemePlayer} from '#/components/capsules/CapsuleThemePlayer'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {Group3_Stroke2_Corner0_Rounded as Group} from '#/components/icons/Group'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import * as Layout from '#/components/Layout'
import {useModuleBoard} from '#/components/modules/useModuleBoard'
import {Text} from '#/components/Typography'
import {type CapsuleModel} from '#/types/capsule'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ModulePlay'>

/** The film a capsule opens on, and the window it plays through. */
type CapsuleFilm = {
  post: AppBskyFeedDefs.PostView
  video: AppBskyEmbedVideo.View
  /** Which millisecond of the film to open on, `0` for *from the start*. */
  startMs: number
  /** How long the window runs from there, `0` to the film's own end. */
  windowMs: number
}

/**
 * A post-shaped view of a capsule that has no post.
 *
 * The film player asks a video for a `post` it never reads — the cover came off the
 * record (`#/lib/capsuleCover`) and nothing downstream fetches the address — so a
 * capsule that belongs to one classroom is given the only post-like thing it has
 * and nothing is fetched. The same shape `CapsulePlay` builds.
 */
function shadowPost(uri: string): AppBskyFeedDefs.PostView {
  return {
    uri,
    cid: '',
    author: {did: new AtUri(uri).hostname, handle: ''},
    record: {},
    indexedAt: '',
  } as unknown as AppBskyFeedDefs.PostView
}

export function ModulePlayScreen({route, navigation}: Props) {
  /** Decoded once: a module's address arrives percent-encoded off a URL. */
  const moduleUri = moduleUriFromPath(route.params.moduleUri) ?? ''
  const {t: l} = useLingui()
  const t = useTheme()
  const insets = useSafeAreaInsets()
  const {currentAccount} = useSession()

  const {data: record, isPending: recordPending} =
    useModuleByUriQuery(moduleUri)
  const items = useMemo(() => record?.items ?? [], [record])

  /*
   * The run, on this device.
   *
   * `progress.resumeIndex` is what makes *play some of it now, the rest later*
   * work, and it is counted against the playlist as it stands rather than against
   * what was stored — so a capsule the creator removed can never strand a learner.
   */
  const {done, progress, markDone, clear} = useModuleRun({moduleUri, items})

  /**
   * Where the run is: a capsule, and which half of it.
   *
   * `null` until the playlist arrives. Seeded once — from the capsule a learner
   * pressed (`route.params.start`), or from where they left off — and never
   * re-seeded, so a later refetch of the record cannot yank the learner back.
   */
  const [step, setStep] = useState<{index: number; stage: ModuleRunStage} | null>(
    null,
  )
  const [finished, setFinished] = useState(false)
  const seededRef = useRef(false)

  const resumeIndex = progress.resumeIndex
  const requestedStart = route.params.start
  useEffect(() => {
    if (seededRef.current) return
    if (items.length === 0) return
    seededRef.current = true
    const index =
      requestedStart != null
        ? Math.max(0, Math.min(requestedStart, items.length - 1))
        : resumeIndex
    setStep({index, stage: 'film'})
  }, [items.length, requestedStart, resumeIndex])

  const current = step ? items[step.index] : undefined

  /*
   * The capsule's record: slides, theme and the chosen cover minute.
   *
   * Read by address for every capsule, published or not — it is the record that
   * holds the questions, and it is also where `coverStartMs` lives, whether the film
   * itself rides on a post or on the record.
   */
  const {
    data: capsuleRecord,
    isPending: capsulePending,
    isFetched: capsuleFetched,
  } = useCapsuleByUriQuery(current?.uri)

  /* The post a published capsule's film rides on; absent for a draft. */
  const {data: postView, isFetched: postFetched} = usePostQuery(current?.postUri)

  const coverStartMs =
    typeof capsuleRecord?.coverStartMs === 'number' && capsuleRecord.coverStartMs > 0
      ? capsuleRecord.coverStartMs
      : 0
  const windowMs = coverStartMs > 0 ? CAPSULE_COVER_MAX_MS : 0

  /**
   * The film, `null` when the capsule has none, `undefined` while it is unknown.
   *
   * The three states are not decoration. *Unknown* is a film still being fetched and
   * must not be read as *no film*, or a capsule would open on a flash of its
   * questions and then jump backwards into a film that had already loaded.
   */
  const film: CapsuleFilm | null | undefined = useMemo(() => {
    if (!current) return undefined
    /* Published: the film is on the post, and the post has to have answered. */
    if (current.postUri) {
      if (postView && AppBskyEmbedVideo.isView(postView.embed)) {
        return {
          post: postView,
          video: postView.embed,
          startMs: coverStartMs,
          windowMs,
        }
      }
      return postFetched ? null : undefined
    }
    /* Record-only: the cover is derived from the embed the record carries. */
    if (!capsuleFetched) return undefined
    const cover = capsuleRecord
      ? capsuleCoverFromEmbed(capsuleRecord.embed, new AtUri(current.uri).hostname)
      : undefined
    if (!cover) return null
    return {
      post: shadowPost(current.uri),
      video: cover,
      startMs: coverStartMs,
      windowMs,
    }
  }, [current, postView, postFetched, capsuleRecord, capsuleFetched, coverStartMs, windowMs])

  /**
   * Which half of the capsule is on screen.
   *
   * The step says where the run *is*; this corrects a *film* step to the questions
   * when the capsule turns out to have no film — the one thing `moduleStepAfterQuiz`
   * cannot know when it hands the next capsule over (see `#/lib/moduleRun`).
   */
  const stage: ModuleRunStage =
    step?.stage === 'film' && film !== undefined
      ? moduleOpeningStage(!!film)
      : (step?.stage ?? 'film')

  const capsuleModel: CapsuleModel | null = useMemo(() => {
    if (!capsuleRecord || !current) return null
    return {
      id: capsuleRecord.uri,
      title: capsuleRecord.title ?? '',
      description: '',
      creatorId: new AtUri(current.uri).hostname,
      themeId: capsuleRecord.themeId ?? 'classic-quiz',
      status: 'published' as const,
      slides: capsuleRecord.slides ?? [],
      behaviorSettings: capsuleRecord.behaviorSettings ?? {},
      metadata: {createdAt: '', updatedAt: ''},
      likeCount: 0,
      likedByMe: false,
    }
  }, [capsuleRecord, current])

  /*
   * The board is asked for *after* the run, and not before.
   *
   * Its only job here is to print the learner's own totals in the summary, and the
   * plays that make them up are only just posted: asking during the run would be a
   * request whose answer is certain to be stale. A finished run asks once.
   */
  const {board} = useModuleBoard({
    moduleUri: finished ? moduleUri : undefined,
    preview: null,
  })
  const myRow = findPlayer(board, currentAccount?.did)

  /** The film handed over: the same capsule's questions are next. */
  const onFilmEnd = useCallback(() => {
    setStep(s => (s ? moduleStepAfterFilm(s.index) : s))
  }, [])

  /**
   * One capsule's questions are over: remember it, then the next film — or the end.
   *
   * This is the whole of the chaining, and it is a callback rather than an effect
   * because the player calls it *after* it has posted the play — so the next capsule
   * can never start before the last one was recorded. `markDone` is called outside
   * the state updater so a double-invoked updater cannot write the run twice.
   */
  const onQuizDone = useCallback(() => {
    if (!step) return
    const item = items[step.index]
    if (item) markDone(item.uri)
    const next = moduleStepAfterQuiz(step.index, items.length)
    if (!next) setFinished(true)
    else setStep(next)
  }, [step, items, markDone])

  const onExit = useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack()
    else navigation.navigate('Module', {moduleUri})
  }, [moduleUri, navigation])

  /** Start the whole run over: forget the finished capsules and return to the top. */
  const onReplay = useCallback(() => {
    clear()
    setFinished(false)
    seededRef.current = true
    setStep({index: 0, stage: 'film'})
  }, [clear])

  const title = record?.title || l`Module`
  const index = step?.index ?? 0
  /* Index past the end of a playlist that shrank under us is a finished run. */
  const reachedEnd = finished || (!!step && items.length > 0 && !current)
  const showPlayer = !reachedEnd && !!current
  /* The film step is the only one whose content is unknown before its record is. */
  const playerLoading =
    showPlayer &&
    (stage === 'film' ? film === undefined : !capsuleModel && capsulePending)

  return (
    <ThemeProvider theme="dark">
      <Layout.Screen
        testID="modulePlayScreen"
        noInsetTop={showPlayer}
        style={showPlayer ? {backgroundColor: 'black'} : undefined}>
        {showPlayer && current ? (
          <View style={[a.flex_1]}>
            {/*
              The capsule, in its two halves.

              Keyed by address so each capsule is a fresh mount: a new film, a new
              set of questions, and none of the previous capsule's state carried in.
            */}
            {stage === 'film' && film ? (
              <CapsuleFilmPlayer
                key={`film-${current.uri}`}
                testID="modulePlayFilm"
                post={film.post}
                video={film.video}
                startMs={film.startMs}
                windowMs={film.windowMs}
                onFilmEnd={onFilmEnd}
              />
            ) : null}

            {stage === 'quiz' && capsuleModel ? (
              <CapsuleThemePlayer
                key={`quiz-${current.uri}`}
                capsule={capsuleModel}
                insetsTop={insets.top + 38}
                onClose={onExit}
                onCapsuleComplete={onQuizDone}
              />
            ) : null}

            {/*
              The run's own strip, over the player.

              It is drawn last so it sits above the capsule's own furniture, and it
              is `pointerEvents="box-none"` so only the close button takes a press —
              everything else on the top of the screen still belongs to the player.
            */}
            <View
              testID="modulePlayStrip"
              pointerEvents="box-none"
              style={[a.absolute, a.inset_0, {paddingTop: insets.top + 6}]}>
              <View
                style={[
                  a.mx_md,
                  a.px_md,
                  a.py_sm,
                  a.rounded_md,
                  a.gap_sm,
                  styles.strip,
                ]}>
                <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                  <Pressable
                    testID="modulePlayClose"
                    accessibilityRole="button"
                    accessibilityLabel={l`Leave the module`}
                    onPress={onExit}
                    style={[styles.closeBtn]}>
                    <X size="sm" fill="white" />
                  </Pressable>
                  <Text
                    numberOfLines={1}
                    style={[a.flex_1, a.text_sm, a.font_bold, styles.onDark]}>
                    {title}
                  </Text>
                  {/* Which half of the capsule this is — the film, or its questions. */}
                  <View
                    testID={`modulePlayStage-${stage}`}
                    style={[styles.stageChip]}>
                    <Text style={[a.text_xs, a.font_bold, styles.onDark]}>
                      {stage === 'film' ? (
                        <Trans>Intro</Trans>
                      ) : (
                        <Trans>Quiz</Trans>
                      )}
                    </Text>
                  </View>
                  <Text
                    testID="modulePlayProgress"
                    style={[a.text_sm, a.font_bold, styles.onDark]}>
                    {l`${Math.min(index + 1, items.length)} / ${items.length}`}
                  </Text>
                </View>

                {/* One segment per capsule: played, playing, ahead. */}
                <View style={[a.flex_row, a.gap_xs]}>
                  {items.map((item, position) => (
                    <View
                      key={item.uri}
                      testID={`modulePlaySegment-${position + 1}`}
                      style={[
                        styles.segment,
                        {
                          backgroundColor: done.includes(item.uri)
                            ? t.palette.primary_500
                            : position === index
                              ? 'rgba(255,255,255,0.85)'
                              : 'rgba(255,255,255,0.25)',
                        },
                      ]}
                    />
                  ))}
                </View>
              </View>
            </View>

            {/* The next capsule's film is on its way: black, not a wrong screen. */}
            {playerLoading ? (
              <View
                testID="modulePlayLoading"
                style={[a.absolute, a.inset_0, a.justify_center, a.align_center]}>
                <Text style={[a.text_md, styles.onDark]}>
                  <Trans>Loading…</Trans>
                </Text>
              </View>
            ) : null}
          </View>
        ) : reachedEnd ? (
          <Layout.Content
            testID="modulePlaySummary"
            contentContainerStyle={[
              a.px_md,
              a.py_3xl,
              a.gap_md,
              a.align_center,
            ]}>
            <View
              style={[
                styles.finishedMark,
                {backgroundColor: t.palette.primary_500},
              ]}>
              <Check size="md" fill="white" />
            </View>
            <Text
              style={[a.text_2xl, a.font_bold, a.text_center, t.atoms.text]}>
              <Trans>Module finished</Trans>
            </Text>
            <Text
              style={[a.text_md, a.text_center, t.atoms.text_contrast_medium]}>
              <Trans>
                You played every capsule in {title}. Your run is counted with the
                class's.
              </Trans>
            </Text>

            <Text
              testID="modulePlaySummaryProgress"
              style={[a.text_sm, a.text_center, t.atoms.text_contrast_medium]}>
              {l`${progress.done} of ${progress.total} capsules finished`}
            </Text>

            {myRow ? (
              <View
                testID="modulePlayTotals"
                style={[
                  a.w_full,
                  a.p_md,
                  a.rounded_md,
                  a.gap_xs,
                  t.atoms.bg_contrast_25,
                ]}>
                <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                  <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
                    {myRow.rank != null ? l`Rank ${myRow.rank}` : l`Finished`}
                  </Text>
                  <View style={[a.flex_1]} />
                  <Text
                    style={[
                      a.text_md,
                      a.font_bold,
                      {color: t.palette.primary_500},
                    ]}>
                    {l`${formatRate(myRow.pointsPerMinute)} pt/min`}
                  </Text>
                </View>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {l`${myRow.points} pts`} · {formatTime(myRow.timeMs)}
                </Text>
              </View>
            ) : (
              <Text
                testID="modulePlayTotalsPending"
                style={[
                  a.text_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  Your totals appear on the board as soon as the class's plays
                  are read.
                </Trans>
              </Text>
            )}

            <Button
              testID="modulePlayLeaderboard"
              label={l`See the leaderboard`}
              size="large"
              color="primary"
              onPress={() =>
                navigation.replace('ModuleLeaderboard', {moduleUri})
              }>
              <ButtonIcon icon={Group} />
              <ButtonText>
                <Trans>See the leaderboard</Trans>
              </ButtonText>
            </Button>

            <Button
              testID="modulePlayAgain"
              label={l`Play it again`}
              size="large"
              color="secondary"
              onPress={onReplay}>
              <ButtonText>
                <Trans>Play it again</Trans>
              </ButtonText>
            </Button>

            <Button
              testID="modulePlayDone"
              label={l`Back to the module`}
              size="large"
              color="secondary"
              onPress={() => navigation.replace('Module', {moduleUri})}>
              <ButtonText>
                <Trans>Back to the module</Trans>
              </ButtonText>
            </Button>
          </Layout.Content>
        ) : (
          /* Nothing to play yet, or nothing to play at all. */
          <Layout.Content
            contentContainerStyle={[
              a.px_md,
              a.py_3xl,
              a.gap_md,
              a.align_center,
            ]}>
            <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
              {recordPending ? (
                <Trans>Loading the module…</Trans>
              ) : (
                <Trans>This module has nothing to play.</Trans>
              )}
            </Text>
            <Button
              label={l`Back to the module`}
              color="secondary"
              size="small"
              onPress={() => navigation.replace('Module', {moduleUri})}>
              <ButtonText>
                <Trans>Back to the module</Trans>
              </ButtonText>
            </Button>
          </Layout.Content>
        )}
      </Layout.Screen>
    </ThemeProvider>
  )
}

const styles = StyleSheet.create({
  strip: {
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
  },
  onDark: {color: 'white'},
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  stageChip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
  },
  segment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
  },
  finishedMark: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
